import React, { useState } from 'react'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { delFetcher, putFetcher } from 'api/swr'
import {
  DotsHorizontalIcon,
  TrashIcon,
  UpdateIcon,
  Pencil1Icon,
  ArrowUpIcon,
} from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { MyFormInput, MyFromCheckbox, MyFormSelect } from 'components/ui/MyFormField'
import { z } from 'zod'
import Loader from '@/components/ui/Loader'
import { FormLabel } from '@/components/ui/form'

export const DataTableRowActions = ({ row }) => {
  const [isEditOpen, setIsEditOpen] = useState(false)
  //   const data = row.original

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setIsEditOpen(true)}>
          <Pencil1Icon className="mr-2 h-4 w-4" />
          <span>Edit</span>
        </DropdownMenuItem>
        <DataTableRowPin row={row} />
        <DataTableRowDelete row={row} />
      </DropdownMenuContent>
      <DataTableRowEdit
        row={row}
        isOpen={isEditOpen}
        onOpenChange={setIsEditOpen}
      />
    </DropdownMenu>
  )
}

const DataTableRowEdit = ({ row, isOpen, onOpenChange }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating, error } = useSWRMutation(
    `/avatar-card-templates/${data.id}`,
    putFetcher,
  )

  const infoFormSchema = z
    .object({
      foregroundLink: z.string().optional(),
      backgroundLink: z.string().optional(),
      cardWidth: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      cardHeight: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      avatarWidth: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      avatarHeight: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      avatarPositionX: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      avatarPositionY: z.preprocess(
        (val) => (val === '' ? undefined : Number(val)),
        z.number().optional(),
      ),
      isAd: z.boolean().optional(),
      iosVersion: z.string().optional(),
      sizeType: z.string().optional(),
    })
    .refine(
      (formData) => {
        // 检查表单中的值（空字符串视为清空）
        const formForeground =
          formData.foregroundLink && formData.foregroundLink.trim() !== ''
            ? formData.foregroundLink.trim()
            : null
        const formBackground =
          formData.backgroundLink && formData.backgroundLink.trim() !== ''
            ? formData.backgroundLink.trim()
            : null

        // 如果表单中两个都为空，检查原始数据
        if (!formForeground && !formBackground) {
          const originalForeground =
            data.foregroundLink && data.foregroundLink.trim() !== ''
          const originalBackground =
            data.backgroundLink && data.backgroundLink.trim() !== ''
          // 如果原始数据也没有，则验证失败
          return originalForeground || originalBackground
        }
        // 如果至少有一个表单值不为空，验证通过
        return true
      },
      {
        message: 'At least one of foregroundLink or backgroundLink is required',
        path: ['foregroundLink'],
      },
    )

  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues: {
      foregroundLink: data.foregroundLink || '',
      backgroundLink: data.backgroundLink || '',
      cardWidth: data.cardWidth || '',
      cardHeight: data.cardHeight || '',
      avatarWidth: data.avatarWidth || '',
      avatarHeight: data.avatarHeight || '',
      avatarPositionX: data.avatarPositionX || '',
      avatarPositionY: data.avatarPositionY || '',
      isAd: data.isAd ?? false,
      iosVersion: data.iosVersion || '',
      sizeType: data.sizeType || 'none',
    },
  })

    const onSubmit = (formData) => {
    if (isMutating) return
    // 只发送有值的字段
    const updates = {}
    Object.keys(formData).forEach((key) => {
      // 如果值是 'none'，视为清空/不选择
      if (
        formData[key] !== '' &&
        formData[key] !== null &&
        formData[key] !== undefined &&
        formData[key] !== 'none'
      ) {
        updates[key] = formData[key]
      }
    })

    trigger(updates, {
      onSuccess: () => {
        mutate(['/avatar-card-templates'])
        onOpenChange(false)
        toast({
          title: 'Template Updated',
          description: 'The template has been successfully updated.',
          className: 'bg-green-500 text-white',
        })
      },
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: 'Update Failed',
          description: err.message || 'Failed to update template.',
        })
      },
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Avatar Card Template</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <MyFormInput
              name="foregroundLink"
              form={form}
              label="Foreground Link (Optional)"
              placeholder="Enter foreground link (optional)"
            />
            <MyFormInput
              name="backgroundLink"
              form={form}
              label="Background Link (Optional)"
              placeholder="Enter background link (optional)"
            />
            <div className="flex items-center space-x-2">
              <FormLabel>Is AD</FormLabel>
              <MyFromCheckbox name="isAd" form={form} />
            </div>
            <MyFormSelect
              name="sizeType"
              form={form}
              label="Size Type"
              items={[
                { name: 'None', code: 'none' },
                { name: 'Vertical', code: 'vertical' },
                { name: 'Square', code: 'square' },
                { name: 'Horizontal', code: 'horizontal' },
              ]}
            />

            <div className="grid grid-cols-2 gap-4">
              <MyFormInput
                type="number"
                name="cardWidth"
                form={form}
                label="Card Width"
                placeholder="Enter card width"
              />
              <MyFormInput
                type="number"
                name="cardHeight"
                form={form}
                label="Card Height"
                placeholder="Enter card height"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <MyFormInput
                type="number"
                name="avatarWidth"
                form={form}
                label="Avatar Width"
                placeholder="Enter avatar width"
              />
              <MyFormInput
                type="number"
                name="avatarHeight"
                form={form}
                label="Avatar Height"
                placeholder="Enter avatar height"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <MyFormInput
                type="number"
                name="avatarPositionX"
                form={form}
                label="Avatar Position X"
                placeholder="Enter avatar position X"
              />
              <MyFormInput
                type="number"
                name="avatarPositionY"
                form={form}
                label="Avatar Position Y"
                placeholder="Enter avatar position Y"
              />
            </div>
            <MyFormInput
              type="number"
              name="iosVersion"
              form={form}
              label="iOS Version (Optional)"
              placeholder="Enter iOS client version (e.g., 1210708)"
            />
            {isMutating ? (
              <Loader>Updating...</Loader>
            ) : (
              <div className="flex gap-2">
                <Button type="submit">Update</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
            {error && (
              <div className="text-red-500">
                {error.message || 'Update failed'}
              </div>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

const DataTableRowPin = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    `/avatar-card-templates/${data.id}/pin`,
    putFetcher,
    {
      onSuccess: () => {
        mutate(['/avatar-card-templates'])
        toast({
          title: 'Template Pinned',
          description: 'The template has been pinned to the top.',
          className: 'bg-green-500 text-white',
        })
      },
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: 'Pin Failed',
          description: err.message || 'Failed to pin template.',
        })
      },
    },
  )

  const handlePin = () => {
    trigger({})
  }

  return (
    <DropdownMenuItem onSelect={handlePin}>
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4" />
          <span>Pinning...</span>
        </>
      ) : (
        <>
          <ArrowUpIcon className="mr-2 h-4 w-4" />
          <span>Pin to Top</span>
        </>
      )}
    </DropdownMenuItem>
  )
}

const DataTableRowDelete = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    '/avatar-card-templates',
    delFetcher,
    {
      onSuccess: () => {
        mutate(['/avatar-card-templates'])
        toast({
          title: 'Template Deleted',
          description: 'The template has been successfully deleted.',
          className: 'bg-green-500 text-white',
        })
      },
    },
  )

  const handleDelete = () => {
    trigger({ id: data.id })
  }

  const confirm = (e) => {
    e.preventDefault()
    toast({
      variant: 'destructive',
      title: 'Delete Template',
      description: 'Are you sure you want to delete this template?',
      action: (
        <ToastAction altText="Try again" onClick={handleDelete}>
          Delete
        </ToastAction>
      ),
    })
  }

  return (
    <DropdownMenuItem onSelect={confirm}>
      {isMutating ? (
        <>
          <UpdateIcon className="mr-2 h-4 w-4" />
          <span>Deleting...</span>
        </>
      ) : (
        <>
          <TrashIcon className="mr-2 h-4 w-4 text-red-500" />
          <span className="text-red-500">Delete</span>
        </>
      )}
    </DropdownMenuItem>
  )
}
